/**
 * Значок связи в шапке главной (макет 15.10.2026, `docs/design/header-2026-10-15`).
 *
 * Со связью всё в порядке — значка нет. Система говорит, что сети нет, —
 * «Нет интернета»; сервер не ответил на проверку — «Нет связи с сервером»:
 * на iPhone так выглядит и авиарежим, система там о сети молчит. Связь
 * вернулась и есть что дослать — «Отправляю», дослали — «Сохранено» на две
 * секунды, и значок уходит.
 *
 * Тап — подсказка, как у (i): главное, что человеку нужно знать без связи, —
 * записанное не потеряется.
 */
import { Icon } from './icon.js';
import { Tip } from './tip.js';

export type NetState = 'ok' | 'offline' | 'server' | 'sending' | 'saved';

const LOOK: Record<Exclude<NetState, 'ok'>, { icon: string; tone: string; label: string }> = {
  offline: { icon: 'wifi-slash', tone: 'warn', label: 'Нет интернета' },
  server: { icon: 'cloud-slash', tone: 'warn', label: 'Нет связи с сервером' },
  sending: { icon: 'arrows-rotate', tone: 'send', label: 'Отправляю' },
  saved: { icon: 'cloud-check', tone: 'done', label: 'Сохранено' },
};

/** Что сказать по тапу. Без аккаунта отправлять некуда — об отправке ни слова. */
function say(state: Exclude<NetState, 'ok'>, account: boolean): string {
  switch (state) {
    case 'offline':
      return account
        ? 'Нет интернета. Всё, что записываешь, сохраняется на телефоне и отправится, когда интернет появится.'
        : 'Нет интернета. Всё, что записываешь, сохраняется на телефоне — работать можно как обычно.';
    case 'server':
      return 'Нет связи с сервером SKOKA. Записи сохраняются на телефоне и отправятся, когда связь появится.';
    case 'sending':
      return 'Связь вернулась — отправляю записи на сервер.';
    case 'saved':
      return 'Всё сохранено на сервере.';
  }
}

export function NetBadge({ state, account }: { state: NetState; account: boolean }) {
  if (state === 'ok') return null;
  const look = LOOK[state];
  return (
    <Tip label={look.label} className={`netbtn ${look.tone}`}
      glyph={<Icon name={look.icon} size={17} {...(state === 'sending' ? { className: 'spin' } : {})} />}>
      {say(state, account)}
    </Tip>
  );
}
